import { useSearchParams } from "react-router";

import { DEFAULT_SORT, EMPTY_FILTERS, TicketFilters, TicketSort } from "@/features/tickets/types/filters";

export function useTicketFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: TicketFilters = {
    statuses: searchParams.getAll("statuses"),
    priorities: searchParams.getAll("priorities"),
    types: searchParams.getAll("types"),
    assignees: searchParams.getAll("assignees"),
    tags: searchParams.getAll("tags"),
  };

  const sort: TicketSort = {
    sortBy: searchParams.get("sort_by") ?? DEFAULT_SORT.sortBy,
    sortDirection: (searchParams.get("sort_direction") ?? DEFAULT_SORT.sortDirection) as "asc" | "desc",
  };

  function applyFilters(next: TicketFilters) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);

        params.delete("statuses");
        params.delete("priorities");
        params.delete("types");
        params.delete("assignees");
        params.delete("tags");

        next.statuses.forEach((v) => params.append("statuses", v));
        next.priorities.forEach((v) => params.append("priorities", v));
        next.types.forEach((v) => params.append("types", v));
        next.assignees.forEach((v) => params.append("assignees", v));
        next.tags.forEach((v) => params.append("tags", v));

        return params;
      },
      { replace: true }
    );
  }

  function applySort(next: TicketSort) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("sort_by", next.sortBy);
        params.set("sort_direction", next.sortDirection);
        return params;
      },
      { replace: true }
    );
  }

  function clearAllFilters() {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete("statuses");
        params.delete("priorities");
        params.delete("types");
        params.delete("assignees");
        params.delete("tags");
        return params;
      },
      { replace: true }
    );
  }

  const hasActiveFilters = filters.statuses.length > 0 || filters.priorities.length > 0 || filters.types.length > 0 || filters.assignees.length > 0 || filters.tags.length > 0;

  return { filters, sort, applyFilters, applySort, clearAllFilters, hasActiveFilters };
}
